import { MailComponentSize } from "@/domain/entities/emails/Email";

export interface TileLayout {
    rowStart: number;
    colStart: number;
    rowSpan: number;
    colSpan: number;
}

function getTileDimensions(size: MailComponentSize): {
    width: number;
    height: number;
} {
    const [width, height] = size.split("x").map(Number);

    return { width, height };
}

export function calculateMasonryLayout(
    items: Array<{ id: string; componentSize: MailComponentSize }>,
    gridCols: number,
    startingRow: number = 0,
): Map<string, TileLayout> {
    const layout = new Map<string, TileLayout>();
    const occupied = new Set<string>();
    let gridHeight = startingRow;

    for (const item of items) {
        const { width, height } = getTileDimensions(item.componentSize);
        let placed = false;
        let safeCounter = 0;

        // Check if dimensions are valid
        if (width > gridCols) {
            console.warn(
                `Item ${item.id} width (${width}) exceeds grid columns (${gridCols})`,
            );
            // Force width to maximum grid size
            const adjustedWidth = gridCols;
            const adjustedHeight = Math.ceil((width * height) / gridCols);

            const fallbackPos: TileLayout = {
                rowStart: gridHeight + 1,
                colStart: 1,
                rowSpan: adjustedHeight,
                colSpan: adjustedWidth,
            };
            layout.set(item.id, fallbackPos);
            gridHeight += adjustedHeight;
            continue;
        }

        // Find the first available position
        for (let row = 0; row <= gridHeight + 10 && !placed; row++) {
            for (let col = 0; col <= gridCols - width && !placed; col++) {
                safeCounter++;

                // Protection against infinite loops
                if (safeCounter > 10000) {
                    console.error(
                        `Layout calculation stuck for item ${item.id}`,
                    );
                    const fallbackPos: TileLayout = {
                        rowStart: gridHeight + 1,
                        colStart: 1,
                        rowSpan: height,
                        colSpan: width,
                    };
                    layout.set(item.id, fallbackPos);
                    gridHeight += height;
                    placed = true;
                    break;
                }

                // Check if this position is free
                let canPlace = true;
                for (let r = 0; r < height && canPlace; r++) {
                    for (let c = 0; c < width && canPlace; c++) {
                        const cellKey = `${row + r},${col + c}`;
                        if (occupied.has(cellKey)) {
                            canPlace = false;
                        }
                    }
                }

                if (canPlace) {
                    // Place the item at this position
                    const position: TileLayout = {
                        rowStart: row + 1, // CSS Grid is 1-indexed
                        colStart: col + 1, // CSS Grid is 1-indexed
                        rowSpan: height,
                        colSpan: width,
                    };
                    layout.set(item.id, position);

                    // Mark cells as occupied
                    for (let r = 0; r < height; r++) {
                        for (let c = 0; c < width; c++) {
                            occupied.add(`${row + r},${col + c}`);
                        }
                    }

                    // Update grid height
                    gridHeight = Math.max(gridHeight, row + height);
                    placed = true;
                }
            }
        }

        // If no placement was found, use a fallback position
        if (!placed) {
            console.warn(
                `Could not place item ${item.id}, using fallback position`,
            );
            const fallbackPos: TileLayout = {
                rowStart: gridHeight + 1,
                colStart: 1,
                rowSpan: height,
                colSpan: width,
            };
            layout.set(item.id, fallbackPos);

            // Mark fallback cells as occupied
            for (let r = 0; r < height; r++) {
                for (let c = 0; c < width; c++) {
                    occupied.add(`${gridHeight + r},${c}`);
                }
            }
            gridHeight += height;
        }
    }

    return layout;
}
